import {
  ActionList,
  ActionListItem,
  Button,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  PageSection,
  TextInput,
  Title,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { PatientCard } from '@app/components/patient/patient-card/patient-card'
import { SelectSinglePatientModal } from '@app/components/patient/patient-search/select-single-patient-modal'
import { usePatient } from '@app/domain/patient/patient.hooks'
import type { IPatientId } from '@app/domain/patient/patient.interface'
import {
  useLocations,
  useVisitDefinitions,
} from '@app/domain/visit/visit.hooks'
import type { IVisitLocation } from '@app/domain/visit/visit.interface'
import { getISODateTime } from '@app/lib/time'
import { ValidationStatusId } from '@app/lib/validators'
import type * as Patients from '@app/state/patients'
import * as Visits from '@app/state/visits'

const PatientContainer = styled.div`
  background-color: white;
  padding: 1em;
  grid-area: patient;
  overflow-wrap: anywhere;
`

const VisitContainer = styled.div`
  background-color: white;
  padding: 1em;
  grid-area: visit;
`

const ActionContainer = styled.div`
  grid-area: actions;
  .pf-c-action-list {
    flex-direction: row-reverse;

    .pf-c-action-list__item {
      margin-left: 0.5em;
    }
  }
`

const GridContaner = styled.div`
  display: grid;
  grid-template-columns: 28em 28em auto;
  grid-template-rows: auto;
  grid-template-areas:
    'patient visit .'
    '. actions .';
  column-gap: 1em;
  row-gap: 1em;
`
export const NewVisitScreen = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [visitDate, setVisitDate] = useState<string>('')
  const [visitDateValidation, setVisitDateValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const [startTime, setStartTime] = useState<string>('')
  const [startTimeValidation, setStartTimeValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const [location, setLocation] = useState<IVisitLocation>(null)
  const [locationValidation, setLocationValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const [definition, setDefinition] = useState<string>(null)
  const [definitionValidation, setDefinitionValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const [patientId, setPatientId] = useState<IPatientId>()
  const { patient, updatePatient } = usePatient(patientId)

  const { locations } = useLocations()
  const { definitions } = useVisitDefinitions(location)

  const isFormValid = useMemo(() => {
    let valid = true

    if (visitDate === '') {
      valid = false
    }

    if (startTime === '') {
      valid = false
    }

    if (location === null) {
      valid = false
    }

    if (definition === null) {
      valid = false
    }

    return valid
  }, [definition, location, startTime, visitDate])

  const createVisit = useCallback(
    (
      patientId: string,
      definitionId: string,
      startTime: string,
      location: string,
    ) => {
      if (isFormValid) {
        dispatch(
          Visits.actions.createVisit(
            patientId,
            definitionId,
            startTime,
            location,
          ),
        )
      }
    },
    [dispatch, isFormValid],
  )

  const handleSetVisitDate = useCallback((newVisitDate: string) => {
    if (newVisitDate === '') {
      setVisitDateValidation(ValidationStatusId.Error)
    } else {
      setVisitDateValidation(ValidationStatusId.Success)
    }
    setVisitDate(newVisitDate)
  }, [])

  const handleSetStartTime = useCallback((newStartTime: string) => {
    if (newStartTime === '') {
      setStartTimeValidation(ValidationStatusId.Error)
    } else {
      setStartTimeValidation(ValidationStatusId.Success)
    }
    setStartTime(newStartTime)
  }, [])

  const handleSetDefinition = useCallback((newDefinition: string | null) => {
    if (newDefinition === null) {
      setDefinitionValidation(ValidationStatusId.Error)
    } else {
      setDefinitionValidation(ValidationStatusId.Success)
    }

    setDefinition(newDefinition)
  }, [])

  const handleSetLocation = useCallback((newLocation: string | null) => {
    setDefinition(null)
    setDefinitionValidation(ValidationStatusId.Default)
    if (newLocation === null) {
      setLocationValidation(ValidationStatusId.Error)
    } else {
      setLocationValidation(ValidationStatusId.Success)
    }

    setLocation(newLocation)
  }, [])

  const handleCreateVisit = useCallback(() => {
    if (patient) {
      const dateTime = getISODateTime(visitDate, startTime)
      createVisit(patient.id, definition, dateTime, location)
    }
  }, [createVisit, definition, location, patient, startTime, visitDate])

  const locationOptions = React.useMemo(
    () =>
      locations?.map((location, idx) => (
        <FormSelectOption key={idx} value={location} label={location} />
      )),
    [locations],
  )

  const handleSelectPatient = useCallback(() => {
    setIsSearchModalOpen(true)
  }, [])

  const handleCancelSelectPatient = useCallback(() => {
    setIsSearchModalOpen(false)
  }, [])

  const handlePatientFound = useCallback((selectedPatient) => {
    setIsSearchModalOpen(false)
    setPatientId(selectedPatient.id)
  }, [])

  const handlePatientUpdated = useCallback(
    async (id: string, updatedPatient: Patients.IApiUpdatePatient) => {
      updatePatient(updatedPatient)
    },
    [updatePatient],
  )

  const handleCancelCreateVisit = useCallback(() => {
    history.goBack()
  }, [history])

  return (
    <PageSection>
      <SelectSinglePatientModal
        confirmButtonLabel={'Select Patient'}
        defaultSearchOptions={{}}
        isModalOpen={isSearchModalOpen}
        onCancel={handleCancelSelectPatient}
        onPatientFound={handlePatientFound}
      />
      <Title headingLevel="h1" size="xl">
        Create New Visit
      </Title>
      <GridContaner>
        <PatientContainer>
          <PatientCard
            isSelectPatientAllowed={true}
            onSelectPatient={handleSelectPatient}
            patient={patient}
            onUpdatePatient={handlePatientUpdated}
          />
        </PatientContainer>
        <VisitContainer>
          <Form isHorizontal>
            <FormGroup
              label="Visit Date"
              isRequired
              validated={visitDateValidation}
              helperTextInvalid="Visit Date is required"
              fieldId="visit-date"
            >
              <TextInput
                isRequired
                type="date"
                id="visit-date"
                name="visit-date"
                value={visitDate}
                onChange={handleSetVisitDate}
                validated={visitDateValidation}
              />
            </FormGroup>
            <FormGroup
              label="Start Time"
              isRequired
              fieldId="visit-start-time"
              validated={startTimeValidation}
              helperTextInvalid="Start Time is required"
            >
              <TextInput
                isRequired
                type={'time'}
                id="visit-start-time"
                name="visit-start-time"
                value={startTime}
                onChange={handleSetStartTime}
                validated={startTimeValidation}
              />
            </FormGroup>
            <FormGroup
              fieldId="location"
              label="Location"
              isRequired
              validated={locationValidation}
              helperTextInvalid="Location is required"
            >
              <FormSelect
                value={location}
                isRequired
                onChange={handleSetLocation}
                validated={locationValidation}
              >
                <FormSelectOption
                  key="empty"
                  value={null}
                  isDisabled
                  selected={location === null}
                  label="Please Choose"
                />
                {locationOptions}
              </FormSelect>
            </FormGroup>
            <FormGroup
              fieldId="visit-definition"
              label="Visit Type"
              isRequired
              validated={definitionValidation}
              helperTextInvalid="Visit type is required"
            >
              <FormSelect
                value={definition}
                isRequired
                onChange={handleSetDefinition}
                validated={definitionValidation}
              >
                <FormSelectOption
                  key="empty"
                  isDisabled
                  selected={definition === null}
                  value={null}
                  label="Please Choose"
                />
                {definitions?.map((defn, idx) => (
                  <FormSelectOption
                    key={idx}
                    value={defn.id}
                    label={defn.name}
                  />
                ))}
              </FormSelect>
            </FormGroup>
          </Form>
        </VisitContainer>
        <ActionContainer>
          <ActionList>
            <ActionListItem>
              <Button
                isBlock
                variant="primary"
                isDisabled={!(patient && isFormValid)}
                onClick={handleCreateVisit}
              >
                Create Visit
              </Button>
            </ActionListItem>
            <ActionListItem>
              <Button
                isBlock
                variant="secondary"
                onClick={handleCancelCreateVisit}
              >
                Cancel
              </Button>
            </ActionListItem>
          </ActionList>
        </ActionContainer>
      </GridContaner>
    </PageSection>
  )
}
